import React, { useState, useEffect, useContext, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Row, Col } from 'react-flexbox-grid'
import moment from 'moment'

import { ExpandableBlocks, ContentBlockLoading, ContentBlock } from 'components/ContentBlock'
import { FilterContext } from 'components/SectionHandler'
import { DatePeriod } from '../../statistics/pages/filterFragments'
import DataTable from 'components/DataTableTest'
import { Text, Number as NumberInput } from 'components/forms'
import Divider from 'components/Divider'
import { useQuery, useLazyQuery } from 'components/graphql'
import { listUserLoyaltyCardBills, listLoyaltyCards } from '../gql'
import useLocale from 'hooks/useLocale'

import { DataWrapper, DataRow } from '../../statistics/pages/ERP/styled'
import { getLoyaltyCardBills, getLoyaltyCardItemBills, getDiscountAllowance } from '../tableColumns'

const Bill = ({ data: { id, monthlyTaxFreeAmount }, mutations: { updateItem } }) => {
	const { frozenUserTime, setFilter } = useContext(FilterContext)
	const { t } = useTranslation()
	const locales = useLocale()
	const [loadLoyaltyCardsList, { data, loading: cardsListLoading, called, queryField }] = useLazyQuery(listLoyaltyCards, { variables: { filter: { userId: id } } })
	const interval = frozenUserTime || {
		fromDateTime: moment().utc().startOf('month').toISOString(),
		toDateTime: moment().utc().endOf('month').toISOString()
	}
	const stitchedBillsList = useRef([])
	const intervalInd = useRef(0)
	// Split selected date interval to the shorter ones to avoid timeout server response
	const splittedInterval = useMemo(() => {
		const outArr = []
		const from = moment(interval.fromDateTime)
		const to = moment(interval.toDateTime)
		const daysInterval = to.diff(from, 'days')
		// for(let i = 0; daysInterval / i >= 1; i += 7) {
		for(let i = 0; i <= daysInterval; i += 7) {
			const toIntermediate = from.clone().utc().add(i + 6, 'days').endOf('day')
			outArr.push({
				fromDateTime: from.clone().add(i, 'days').toISOString(),
				toDateTime: toIntermediate.isAfter(to) ? to.toISOString() : toIntermediate.toISOString()
			})
		}
		return outArr
	}, [interval])
	// Default value for listBillsQueryField, because on refetch listBillsQueryField is undefined
	const { loading: billsListLoading, refetch, queryField: listBillsQueryField = 'listUserLoyaltyCardBills' } = useQuery(listUserLoyaltyCardBills, {
		variables: {
			userId: id,
			filter: { period: { from: splittedInterval[intervalInd.current].fromDateTime, to: splittedInterval[intervalInd.current].toDateTime } },
			params: {
				cursors: { limit: 500 }
			}
		},
		onCompleted: result => {
			const { cursors } = result?.cashAssist?.[listBillsQueryField] || {}
			const dataPart = result?.cashAssist?.[listBillsQueryField]?.items || []
			stitchedBillsList.current = [...stitchedBillsList.current, ...dataPart]
			if(cursors?.hasNext) {
				refetch({
					params: {
						cursors: { after: cursors?.after, limit: 500 }
					},
				})
			} else if(intervalInd.current + 1 >= splittedInterval.length) {
				setDataState({
					...dataState,
					data: stitchedBillsList.current,
					freezedData: !dataState.freezedData.length ? stitchedBillsList.current : dataState.freezedData
				})
				stitchedBillsList.current = []
			} else {
				intervalInd.current += 1
				refetch({
					userId: id,
					filter: { period: { from: splittedInterval[intervalInd.current].fromDateTime, to: splittedInterval[intervalInd.current].toDateTime } }
				})
			}
		}
	})
	const [dataState, setDataState] = useState({
		data: [],
		freezedData: [],
	})
	const [inputFields, setInputFields] = useState({
		month: monthlyTaxFreeAmount || null,
		year: monthlyTaxFreeAmount * 12 || null
	})
	const [total, setTotal] = useState('')
	const [selectedBill, setSelectedBill] = useState(null)

	const cradsBlockId = useMemo(() => 'loyaltyCardsBlock', [])
	const handleExpandBlocks = blockId => {
		if(blockId === cradsBlockId && !called) {
			loadLoyaltyCardsList()
		}
	}

	const getDiscount = (items) => {
		const discount = items?.reduce((acc, item) => {
			const { flag, price, amount, discountPercent } = item
			if(flag !== 'Deleted' && discountPercent !== 0) {
				// eslint-disable-next-line no-param-reassign
				acc += (amount * price * (discountPercent / 100))
			}
			return acc
		}, 0)
		return discount
	}

	const getDiscountAllowanceByEachMonth = () => {
		let groupKey = 0

		const discountFilteredByMonth = dataState?.freezedData
			.filter(i => +moment(i.dateTime).format('YYYY') === moment().year())
			.reduce((acc, i) => {
				let m = i.date.split(('-'))[1]
				// eslint-disable-next-line no-plusplus
				acc[m] ? acc[m].data.push(i) : acc[m] = { group: String(groupKey++), data: [i] }
				return acc
			}, {})

		const result = Object.keys(discountFilteredByMonth)
			.map(i => discountFilteredByMonth[i])
			.sort((a, b) => a.group > b.group ? 1 : -1)
			.map(i => ({
				...i,
				month: moment().month(+i.data[0].date.split(('-'))[1] - 1).locale(locales).format('MMMM'),
				based: i.data.map(el => el.items).map(el => getDiscount(el)).reduce((acc, el) => acc + el, 0)
			}))
		return result
	}

	const getTableData = () => {
		const dataTemplate = []
		const discountAllowance = getDiscountAllowanceByEachMonth()

		for (let i = 0; i < 12; i++) {
			const monthVal = moment().month(i).locale('de').format('MMMM')
			dataTemplate.push({ month: monthVal, based: 0, balance: typeof inputFields.month === 'number' ? inputFields.month : '-' })
		}

		const tableData = Object.values([...dataTemplate, ...discountAllowance]
			.reduce((result, { month, ...rest }) => {
				result[month] = {
					...(result[month] || {}),
					month,
					...rest
				}
				return result
			}, {}))

		if(typeof inputFields.month === 'number') {
			const balance = tableData.map(i => i.balance)
			const based = tableData.map(i => i.based)
			const subtraction = balance.map((num, index) => num - based[index])
			const sumBalance = subtraction.map((elem, index) => subtraction.slice(0, index + 1).reduce((a, b) => (a + b)))
			const calcTableData = tableData.map((el, index) => {
				return ({
					...el,
					balance: sumBalance[index]
				})
			})
			return calcTableData
		}

		return tableData
	}

	const minMaxVal = ({ min: minVal = 0, max: maxVal, error = `${t('validators.value_between', { minVal, maxVal })}` }) => {
		if(isNaN(+minVal) || isNaN(+maxVal)) {
			return {}
		}
		const validate = value => {
			const val = +value
			if(isNaN(val)) {
				return null
			}
			if(val < +minVal || val > +maxVal) {
				return error
			}
			return value
		}
		return {
			validate
		}
	}

	const handleChangeYear = (value) => {
		const divideVal = value / 12
		setInputFields({
			 ...inputFields,
			 year: minMaxVal({ max: 1080 }).validate(value),
			 month: value <= 1080 ? (Math.round(divideVal) || null) : null
		})
	}

	const handleChangeMonth = (value) => {
		const multiplyVal = value * 12
		setInputFields({
			 ...inputFields,
			 month: minMaxVal({ max: 90 }).validate(value),
			 year: value <= 90 ? (multiplyVal || null) : null
		})
	}

	const handleChange = ({ from, to }) => {
		intervalInd.current = 0
		setFilter('frozenUserTime', {
			fromDateTime: from,
			toDateTime: to
		})
	}

	useEffect(() => {
		const totalDiscount = dataState?.data
			.map(i => i.items)
			.map(i => getDiscount(i))
			.reduce((acc, i) => acc + i, 0)
			.toLocaleString(locales, {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2
			})
		setTotal(totalDiscount)
	}, [dataState?.data])

	return(
		<ExpandableBlocks onExpand={handleExpandBlocks}>
			<ContentBlockLoading
				blockId={cradsBlockId}
				isLoading={cardsListLoading}
				label={t('user_section.strings.linked_cards')}
			>
				<Row>
					<Col xs={6}>
						{data[queryField]?.items.length ? data[queryField]?.items.map(({ id: cardNumber, name }) => (
							<DataWrapper key={cardNumber}>
								<DataRow>
									<span>{cardNumber}:</span>
									<span>{name}</span>
								</DataRow>
							</DataWrapper>
						)) : <span>{t('user_section.strings.card_list_empty')}</span>}
					</Col>
				</Row>
			</ContentBlockLoading>
			<ContentBlockLoading
				label={t('user_section.fields.evaluation')}
				unmountOnCollapse
				alwaysExpanded
				isLoading={billsListLoading}
			>
				<Row>
					<Col xs={11}>
						<DatePeriod
							value={interval}
							onChange={handleChange}
							label={t('common.strings.area')}
							labelPeriod={{
								from: `${t('common.strings.area')} ${t('common.strings.from').toLowerCase()}`,
								to: `${t('common.strings.area')} ${t('common.strings.to').toLowerCase()}`
							}}
						/>
					</Col>
					<Col xs={1}>
						<Text
							noForm
							label={t('user_section.fields.discount_allowance')}
							value={total}
							readOnly
						/>
					</Col>
				</Row>
				<Divider visible/>
				<Row>
					<Col xs={6}>
						<DataTable
							columns={getLoyaltyCardBills(t)}
							onSelect={rowData => setSelectedBill(rowData)}
							height={500}
							withSelectRow
							withSearch
							sortBy={{ id: 'date', desc: false }}
							data={dataState.data || []}
						/>
					</Col>
					{selectedBill &&
						<Col xs={6}>
							<DataTable
								height={500}
								columns={getLoyaltyCardItemBills(t, locales)}
								data={selectedBill?.items || []}
							/>
						</Col> }
				</Row>
			</ContentBlockLoading>
			<ContentBlock unmountOnCollapse label={t('user_section.fields.discount_allowance')}>
				<Row>
					<Col xs={3}>
						<NumberInput
							withControls={false}
							noForm
							label={t('user_section.fields.per_year')}
							inline={true}
							value={inputFields.year}
							onChange={handleChangeYear}
							onBlur={() => updateItem({ monthlyTaxFreeAmount: inputFields.month })}
							error={typeof inputFields.year === 'string' ? inputFields.year : false}
						/>
					</Col>
					<Col xs={3}>
						<NumberInput
							withControls={false}
							noForm
							label={t('user_section.fields.per_month')}
							inline={true}
							value={inputFields.month}
							onChange={handleChangeMonth}
							onBlur={({ target: { value } }) => updateItem({ monthlyTaxFreeAmount: +value })}
							error={typeof inputFields.month === 'string' ? inputFields.month : false}
						/>
					</Col>
				</Row>
				<Divider/>
				<Row>
					<Col xs={6}>
						<DataTable
							height={470}
							columns={getDiscountAllowance(t, locales, inputFields.month) || []}
							data={getTableData() || []}
						/>
					</Col>
				</Row>
			</ContentBlock>
		</ExpandableBlocks>
	)
}

export default Bill
